import React from 'react';
import { CheckCircle, AlertTriangle } from 'lucide-react';

/**
 * Le contrôle de lecture de la liste officielle (lectureListeOfficielle.js,
 * controleLecture) : a-t-on lu toutes les lignes d'élèves du document ?
 *
 * C'est ce qui aurait fait sauter aux yeux la panne du CEPE 2026 : 32
 * matricules dans le document, 0 élève lu avec son nom. Vert quand tout
 * concorde, ambre sinon - jamais bloquant.
 *
 * Les lignes en cause sont nommées ici même, pas seulement comptées : la
 * personne n'a pas à les chercher dans les grandes listes (demande de Daniel
 * du 01/11/2026). Un matricule lu avec une autre écriture (un espace, un
 * caractère en trop) n'est pas une ligne perdue : il est montré, et le
 * contrôle reste vert.
 */
const pluriel = (n, mot, motPluriel = `${mot}s`) => `${n} ${n > 1 ? motPluriel : mot}`;

const nomLu = (ligne) => ligne?.nom_complet || [ligne?.nom, ligne?.prenom].filter(Boolean).join(' ') || ligne?.texte || '-';

const Detail = ({ titre, children }) => (
  <div className="mt-2">
    <p className="text-xs font-semibold">{titre}</p>
    <ul className="mt-1 space-y-1">{children}</ul>
  </div>
);

const Element = ({ children }) => (
  <li className="rounded-lg border border-black/5 bg-white/70 px-3 py-1.5 text-xs text-gray-800">{children}</li>
);

const Matricule = ({ valeur }) => <span className="font-mono font-semibold">{valeur}</span>;

const ControleLecture = ({ controle, className = '' }) => {
  if (!controle) return null;

  if (!controle.methode) {
    return (
      <div className={`rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-gray-600 ${className}`}>
        Contrôle de lecture impossible : la liste n&apos;a ni matricule ni date de naissance. Vérifiez l&apos;aperçu.
      </div>
    );
  }

  const manquants = controle.manquants || [];
  const ecritsAutrement = controle.ecritsAutrement || [];
  const doublons = controle.doublons || [];
  const sansForme = controle.sansForme || [];
  const anomalies = controle.anomalies || [];

  let message;
  if (controle.methode === 'matricule') {
    const retrouves = controle.document - manquants.length;
    if (controle.ok && ecritsAutrement.length === 0) {
      message = `${pluriel(controle.document, 'matricule')} de la forme ${controle.exemple} dans tout le document, `
        + `${pluriel(controle.lus, 'élève lu', 'élèves lus')} avec son matricule. Aucune ligne perdue.`;
    } else if (controle.ok) {
      message = `${pluriel(controle.document, 'matricule')} de la forme ${controle.exemple} dans tout le document : `
        + `${controle.lus} lu${controle.lus > 1 ? 's' : ''} tel${controle.lus > 1 ? 's' : ''} quel${controle.lus > 1 ? 's' : ''}, `
        + `${ecritsAutrement.length} lu${ecritsAutrement.length > 1 ? 's' : ''} avec une autre écriture (ci-dessous). Aucune ligne perdue.`;
    } else {
      message = `${pluriel(controle.document, 'matricule')} de la forme ${controle.exemple} dans le document, `
        + `${retrouves} retrouvé${retrouves > 1 ? 's' : ''} parmi les élèves lus.`
        + (manquants.length > 0 ? ` ${manquants.length > 1 ? 'Ces lignes du document n\'ont' : 'Cette ligne du document n\'a'} pas été lue${manquants.length > 1 ? 's' : ''} comme élève :` : '')
        + (doublons.length > 0 ? ' Un matricule a été lu deux fois.' : '');
    }
  } else {
    message = controle.ok
      ? `${pluriel(controle.lus, 'élève lu', 'élèves lus')}, une date de naissance chacun.`
      : `${pluriel(controle.lus, 'élève lu', 'élèves lus')} pour ${pluriel(controle.document, 'date')} de naissance. `
        + 'Ces lignes n\'ont pas exactement une date - deux élèves lus sur une même ligne, ou une date manquante :';
  }

  return (
    <div
      className={`flex items-start gap-2 rounded-lg border p-3 text-sm ${controle.ok
        ? 'border-green-200 bg-green-50 text-green-900'
        : 'border-amber-200 bg-amber-50 text-amber-900'
        } ${className}`}
    >
      {controle.ok
        ? <CheckCircle size={16} className="mt-0.5 flex-shrink-0 text-green-700" />
        : <AlertTriangle size={16} className="mt-0.5 flex-shrink-0 text-amber-700" />}
      <div className="min-w-0 flex-1">
        <p>
          <span className="font-semibold">Contrôle de lecture</span>
          {' - '}
          {message}
        </p>

        {manquants.length > 0 && (
          <Detail titre={`Dans le document, mais chez aucun élève lu (${manquants.length})`}>
            {manquants.map((m, i) => (
              <Element key={`${m.matricule}-${i}`}>
                <Matricule valeur={m.matricule} />
                <span className="text-gray-500"> - ligne du document : </span>
                « {m.texte} »
              </Element>
            ))}
          </Detail>
        )}

        {ecritsAutrement.length > 0 && (
          <Detail titre={`Lus avec une autre écriture du matricule (${ecritsAutrement.length})`}>
            {ecritsAutrement.map((m) => (
              <Element key={m.matricule}>
                Ligne {m.ligne.ligne}, {nomLu(m.ligne)}
                <span className="text-gray-500"> - matricule lu « {m.ligne.matricule_national || 'vide'} » pour </span>
                <Matricule valeur={m.matricule} />
              </Element>
            ))}
          </Detail>
        )}

        {doublons.length > 0 && (
          <Detail titre={`Lus deux fois (${doublons.length})`}>
            {doublons.map((m) => (
              <Element key={m}><Matricule valeur={m} /> - le document ne le porte qu&apos;une fois.</Element>
            ))}
          </Detail>
        )}

        {sansForme.length > 0 && (
          <Detail titre={`Élèves lus sans matricule de la forme ${controle.exemple} (${sansForme.length})`}>
            {sansForme.map((l) => (
              <Element key={l.ligne}>
                Ligne {l.ligne}, {nomLu(l)}
                <span className="text-gray-500"> - matricule lu « {l.matricule_national || 'vide'} »</span>
              </Element>
            ))}
          </Detail>
        )}

        {anomalies.length > 0 && (
          <Detail titre={`Lignes à vérifier (${anomalies.length})`}>
            {anomalies.map((a) => (
              <Element key={a.ligne.ligne}>
                Ligne {a.ligne.ligne}, {nomLu(a.ligne)}
                <span className="text-gray-500"> - {a.dates === 0 ? 'aucune date lue' : `${a.dates} dates lues`}</span>
              </Element>
            ))}
          </Detail>
        )}
      </div>
    </div>
  );
};

export default ControleLecture;
